/**
 * SKIN-262 — map the visit QC read (SKIN-256) into the shapes the review components use.
 *
 * Pure functions so the container stays a wiring layer and the mapping is unit-tested
 * without axios / decrypt / React.
 */
import { IdentifiableCorrection } from './ImageReviewCard';
import { ImageAssessmentValue } from './ImageAssessment';
import { LesionFeedbackValue } from './LesionPanel';
import { QcOutcome, ReviewImage } from './imageMeta';
import { VisitShot } from './VisitShotsPanel';
import { QcCategoriesState } from './qcCategories';
import {
    QcImageStateDto,
    QcLesionStateDto,
    QcVisitShotDto,
    QcVisitStateDto,
} from '../../../services/qualityCheck';

const asOutcome = (raw: string | null | undefined): QcOutcome | null => {
    if (raw === 'correct' || raw === 'needs') return raw;
    return null;
};

/** Images that can sit in a LesionReviewSection (need a lesion + a field name). */
export function reviewableImages(images: QcImageStateDto[]): QcImageStateDto[] {
    return images.filter((image) => !!image.groupRecordUuid && !!image.imageFieldName);
}

export function toReviewImages(
    images: QcImageStateDto[],
    lesions: QcLesionStateDto[],
): ReviewImage[] {
    const lesionByUuid = new Map(lesions.map((lesion) => [lesion.groupRecordUuid, lesion]));
    return reviewableImages(images).map((image) => {
        const lesion = image.groupRecordUuid
            ? lesionByUuid.get(image.groupRecordUuid)
            : undefined;
        return {
            encryptedFileUuid: image.encryptedFileUuid,
            localId: image.localId,
            groupRecordUuid: image.groupRecordUuid as string,
            fieldName: image.imageFieldName as string,
            target: lesion?.target ?? null,
            bodyRegion: lesion?.bodyRegion ?? null,
            qualityScore: image.qualityScore,
            identifiable: image.identifiable,
            autoIdentifiable: image.autoIdentifiable,
            bytes: { state: 'loading' },
        };
    });
}

/**
 * SKIN-618 — each required visit-level shot, joined to the photograph that answers it.
 * Presence only: the shot is never reviewed, so nothing of a verdict is carried. Its
 * photograph is not in `reviewableImages` (it hangs off no lesion), which is what keeps
 * it out of the rail's count — the API does not wait on a verdict for it either.
 */
export function toVisitShots(
    shots: QcVisitShotDto[] | undefined,
    images: QcImageStateDto[],
): VisitShot[] {
    const imageByLocalId = new Map(images.map((image) => [image.localId, image]));
    return (shots ?? []).map(({ fieldName, localIds, skipped }) => {
        const image = localIds.map((localId) => imageByLocalId.get(localId)).find(Boolean);
        return {
            fieldName,
            image: image
                ? {
                    encryptedFileUuid: image.encryptedFileUuid,
                    localId: image.localId,
                    identifiable: image.identifiable,
                    bytes: { state: 'loading' },
                }
                : null,
            skipped: skipped ?? null,
        };
    });
}

export function toAssessmentMap(
    images: QcImageStateDto[],
): Record<string, ImageAssessmentValue> {
    const map: Record<string, ImageAssessmentValue> = {};
    for (const image of reviewableImages(images)) {
        map[image.encryptedFileUuid] = {
            outcome: asOutcome(image.entry?.outcome ?? null),
            categoryIds: image.entry?.categoryIds ?? [],
            comment: image.entry?.comment ?? '',
        };
    }
    return map;
}

export function toLesionValueMap(
    lesions: QcLesionStateDto[],
): Record<string, LesionFeedbackValue> {
    const map: Record<string, LesionFeedbackValue> = {};
    for (const lesion of lesions) {
        map[lesion.groupRecordUuid] = {
            categoryIds: lesion.entry?.categoryIds ?? [],
            comment: lesion.entry?.comment ?? '',
        };
    }
    return map;
}

/** Latest correction per image, shaped for ImageReviewCard's audit line. */
export function toCorrectionsMap(
    images: QcImageStateDto[],
): Record<string, IdentifiableCorrection> {
    const map: Record<string, IdentifiableCorrection> = {};
    for (const image of images) {
        const latest = image.corrections?.[0];
        if (!latest) continue;
        const actorName = [latest.actor?.name, latest.actor?.surnames]
            .filter(Boolean)
            .join(' ')
            .trim() || '—';
        map[image.encryptedFileUuid] = {
            actorName,
            correctedAt: latest.at,
            reason: latest.reason,
            // Written corrections always carry boolean endpoints; coerce null for the card type.
            from: latest.from === true,
            to: latest.to === true,
        };
    }
    return map;
}

export function lesionCategoriesFromVisit(visit: QcVisitStateDto): QcCategoriesState {
    return { status: 'ready', sections: visit.lesionCategorySections };
}

export function emptyAssessment(): ImageAssessmentValue {
    return { outcome: null, categoryIds: [], comment: '' };
}

export function emptyLesionValue(): LesionFeedbackValue {
    return { categoryIds: [], comment: '' };
}
